import { useCallback, useMemo, useState } from "react";
import { makeStyles } from "tss-react/mui";
import { ATTEMPT_STATUSES } from "@miplatform/constants";
import { Box } from "@mui/material";
import {
  CalendarLockProvider,
  SchedulerViewType,
  useCalendarContext,
} from "../../../providers";
import { CUSTOM_WORK_ORDER_NAME } from "../CreateWorkOrderDrawer/CreateWorkOrderDrawer.constants";
import { AssignmentView } from "./AssignmentView";
import {
  CalendarVerticalTabs,
  type TabGroupItem,
} from "./CalendarVerticalTabs";
import { SchedulerView } from "./SchedulerView";
import { ASSIGNMENT_MODES } from "./useAssignmentStaging";
import { useNHCPriorityScheduler } from "./useNHCPriorityScheduler";
import {
  DEFAULT_GROUP,
  TabGroupKey,
  useNHCPrioritySchedulerTabs,
} from "./useNHCPrioritySchedulerTabs";

export function NHCPriorityScheduler() {
  return (
    <CalendarLockProvider>
      <NHCPrioritySchedulerContent />
    </CalendarLockProvider>
  );
}

export function NHCPrioritySchedulerContent() {
  const { classes } = useStyles();
  const [activeGroup, setActiveGroup] = useState<TabGroupKey>(DEFAULT_GROUP);

  const {
    events,
    calendarSet,
    isAssigneeMode,
    setVerticalTabActiveFilter,
    setActiveView,
  } = useCalendarContext();
  const scheduler = useNHCPriorityScheduler();
  const workTemplateBases = useMemo(
    () =>
      calendarSet?.config?.workTemplateBases?.map((template) =>
        template.isCustom
          ? { ...template, name: CUSTOM_WORK_ORDER_NAME }
          : template
      ) || [],
    [calendarSet?.config?.workTemplateBases]
  );
  const doesCustomWorkOrderExist = useMemo(
    () =>
      events
        .filter(({ attemptStatus }) => attemptStatus !== ATTEMPT_STATUSES.HOLD)
        .some((event) => event.isCustomWorkOrder),
    [events]
  );
  const tabsWithItems = useNHCPrioritySchedulerTabs(
    workTemplateBases,
    events,
    scheduler.rawUnassignedWorkOrders,
    scheduler.rawNotReadyWorkOrders,
    doesCustomWorkOrderExist
  );

  const handleSelectionChange = useCallback(
    (group: TabGroupKey) => {
      setActiveGroup(group);
      setVerticalTabActiveFilter(null);
      setActiveView(tabsWithItems[group].view);
    },
    [tabsWithItems, setActiveView, setVerticalTabActiveFilter]
  );

  const handleSubItemPress = useCallback(
    (group: TabGroupKey, _item: TabGroupItem) => {
      setVerticalTabActiveFilter(_item.value);
      setActiveView(tabsWithItems[group].view);
    },
    [setActiveView, setVerticalTabActiveFilter, tabsWithItems]
  );

  const activeView = isAssigneeMode
    ? SchedulerViewType.Scheduler
    : tabsWithItems[activeGroup].view;
  const assignmentMode =
    activeGroup === "Not Ready"
      ? ASSIGNMENT_MODES.NOT_READY
      : ASSIGNMENT_MODES.UNASSIGNED;
  const assignmentWorkOrders =
    assignmentMode === ASSIGNMENT_MODES.NOT_READY
      ? scheduler.rawNotReadyWorkOrders
      : scheduler.rawUnassignedWorkOrders;

  return (
    <Box className={classes.container}>
      {!isAssigneeMode && (
        <CalendarVerticalTabs
          groups={tabsWithItems}
          defaultGroup={activeGroup}
          onSelectionChange={handleSelectionChange}
          onSubItemPress={handleSubItemPress}
        />
      )}
      <Box className={classes.mainView}>
        {activeView === SchedulerViewType.Assignment && (
          <AssignmentView
            scheduler={scheduler}
            assignmentMode={assignmentMode}
            workOrders={assignmentWorkOrders}
          />
        )}
        {activeView === SchedulerViewType.Scheduler && <SchedulerView />}
      </Box>
    </Box>
  );
}

const useStyles = makeStyles()((theme) => ({
  container: {
    display: "flex",
    padding: theme.spacing(1),
    height: "100%",
    width: "100%",
    gap: theme.spacing(2),
    position: "relative",
  },
  mainView: {
    display: "flex",
    overflow: "auto",
    minHeight: 0,
    width: "100%",
  },
}));
